// ─────────────────────────────────────────────────────────────────────────
// 배포 사이에 열려 있던 화면의 "조각 못 받음" 복구 (2026-09-30 담당자: "자동복구 넣어줘")
//
// 화면 대부분은 누를 때 받는다(React.lazy). 학생이 사이트를 켜 둔 채 새 버전이 배포되면
// 그 화면은 옛 버전이라 옛 조각 주소를 부르는데, 서버에는 새 조각만 있다 — Firebase 는
// 없는 파일에 index.html 을 돌려주고(`**` → index.html), webpack 은 ChunkLoadError 를 낸다.
// 받아 주는 곳이 없으면 React 가 트리를 통째로 내려 **하얀 화면**이 된다.
//
// 그래서 한 번만 새로고침한다(새 index.html → 새 조각). 무한 반복은 이렇게 막는다:
// 새로고침하기 전에 **지금 페이지의 main.js 주소**를 적어 둔다. 새로고침한 뒤 또 실패했을 때
//   - main.js 가 그대로다 → 새로고침해도 새 버전이 안 왔다(망·캐시 문제). 다시 하지 않고 버튼을 보인다.
//   - main.js 가 바뀌었다 → 그 사이 또 배포된 것이다. 한 번 더 새로고침해도 된다.
// 시간 상수를 쓰지 않는다 — "몇 초 안에 두 번"은 지어낸 숫자이고, 판정할 사실(버전)이 따로 있다.
//
// 인터넷이 끊겨 못 받은 것이면 새로고침하지 않는다 — 브라우저 오프라인 화면이 나와
// 하얀 화면보다 나쁘다. 연결을 확인하라고 말하고 버튼을 둔다.
// ─────────────────────────────────────────────────────────────────────────

const RELOADED_FROM_KEY = 'semifab-chunk-reload-from';

/** webpack 이 조각을 못 받았을 때 내는 오류인가 */
export function isChunkLoadError(err) {
  if (!err) return false;
  if (err.name === 'ChunkLoadError') return true;
  const msg = String(err.message || '');
  return /Loading (CSS )?chunk [\w-]+ failed/.test(msg);
}

/** 지금 페이지가 띄운 버전 — main.js 주소(내용 해시가 들어 있다) */
export function currentBuildId(doc = typeof document !== 'undefined' ? document : null) {
  if (!doc) return null;
  const s = doc.querySelector('script[src*="/static/js/main."]');
  return s ? s.getAttribute('src') : null;
}

/**
 * 조각을 못 받았을 때 무엇을 할지 정한다(부작용 없음 — 테스트용으로 갈라 둔다).
 * @returns {'reload'|'offline'|'stale'}
 *   reload  : 새로고침하면 된다
 *   offline : 인터넷이 끊겼다 — 새로고침하지 않는다
 *   stale   : 이 버전에서 이미 새로고침했는데 또 실패했다(또는 기록을 못 남긴다) — 버튼만
 */
export function decideChunkRecovery({ online, buildId, reloadedFrom, storageOk }) {
  if (online === false) return 'offline';
  if (!storageOk) return 'stale'; // 기록을 못 남기면 반복을 못 막는다 — 자동으로 하지 않는다
  if (reloadedFrom != null && reloadedFrom === (buildId || '')) return 'stale';
  return 'reload';
}

/**
 * 조각을 못 받았다 — 할 수 있으면 한 번 새로고침한다.
 * @returns {'reload'|'offline'|'stale'} 실제로 한 일(reload 면 곧 페이지가 바뀐다)
 */
export function recoverFromChunkError({ win = typeof window !== 'undefined' ? window : null } = {}) {
  if (!win) return 'stale';
  const buildId = currentBuildId(win.document);
  let reloadedFrom = null;
  let storageOk = true;
  try { reloadedFrom = win.sessionStorage.getItem(RELOADED_FROM_KEY); } catch { storageOk = false; }
  const online = win.navigator ? win.navigator.onLine : undefined;
  const action = decideChunkRecovery({ online, buildId, reloadedFrom, storageOk });
  if (action === 'reload') {
    try { win.sessionStorage.setItem(RELOADED_FROM_KEY, buildId || ''); } catch { return 'stale'; }
    win.location.reload();
  }
  return action;
}

export const __test__ = { RELOADED_FROM_KEY };
